import { isClientType } from '@apm/contracts';
import type { ClientType } from '@apm/contracts';
import type { MessageKey } from './i18n/ko.js';

/**
 * 재생 클라이언트 유형 판정 (`docs/08` §1 의 `ClientType`).
 *
 * **이 값 하나가 캐스케이드 전체를 가른다.** `mobile_web` 이면 Tier 0 에서 끝나고
 * (재생 기기가 곧 구매 기기다), 아니면 Tier 2 의 Push 나 Tier 4 의 QR 로 간다.
 * 틀리면 증상이 조용하다 — 폰에서 보고 있는데 QR 이 뜨거나(자기 화면을 자기가 스캔할 수
 * 없다), PC 에서 보고 있는데 알림도 QR 도 없이 상품 시트만 열린다.
 *
 * > **B9 이전에는 「PC 인데 `mobile_web` 으로 판정되면 재생이 끝난다」였다.** 그때 Tier 0
 * > 은 `location.href` 로 페이지를 옮겼기 때문이다. 규칙 14 가 개정되고 시트가 생긴 지금
 * > 오판의 대가는 **잘못된 화면**이지 재생 종료가 아니다 — 그래도 여전히 오판이다.
 *
 * **순수 함수로 두는 이유가 그것이다.** `bloom` 은 node 환경에서 테스트하고
 * (jsdom 미도입) DOM 을 만지면 이 판정이 검사에서 통째로 빠진다. 값을 읽는 일은
 * `detectClientTypeFromWindow()` 한 줄이 맡는다.
 */
export interface ClientTypeSignals {
  /** `navigator.userAgent` */
  userAgent: string;
  /** `matchMedia('(pointer: coarse)').matches` — 손가락으로 누르는 화면인가 */
  pointerCoarse: boolean;
  /** `window.innerWidth` (CSS 픽셀) */
  viewportWidth: number;
}

/**
 * TV·셋톱 계열 표식. **가장 먼저 본다** — Android TV 의 UA 에는 `Android` 가 들어 있어
 * 순서를 뒤집으면 거실 TV 가 `mobile_web` 이 되고, 그러면 Tier 0 으로 떨어져 시청자의
 * TV 화면이 상품 페이지로 넘어가 버린다 (재생이 끝난다).
 */
const TV_MARKERS = [
  'smart-tv',
  'smarttv',
  'googletv',
  'android tv',
  'appletv',
  'tizen',
  'web0s',
  'webos',
  'netcast',
  'hbbtv',
  'bravia',
  'aft', // Amazon Fire TV — AFTB·AFTM·AFTS …
  'crkey', // Chromecast
  'vidaa',
  'roku',
  'playstation',
  'xbox',
];

/** 모바일 표식. `Mobile` 이 없는 안드로이드 태블릿도 여기 걸린다 (아래 주석). */
const MOBILE_MARKERS = ['iphone', 'ipod', 'android', 'mobile', 'silk', 'kindle', 'blackberry'];

/**
 * 태블릿까지 포함하는 가로 상한(CSS 픽셀). UA 로 판별되지 않는 기기의 마지막 근거다.
 *
 * 좁게 잡으면 태블릿 가로 모드가 `pc_web` 이 되고, 넓게 잡으면 터치스크린 노트북이
 * `mobile_web` 이 된다. **두 신호를 함께 요구해서** 폭 하나로 정하지 않는다.
 */
const TOUCH_MAX_WIDTH = 1024;

/**
 * 신호 셋에서 유형 하나를 낸다.
 *
 * **`paytv_stb` 는 여기서 나오지 않는다.** 그것은 파트너사의 네이티브 셋톱 클라이언트가
 * 스스로 말하는 값이고(계약의 기본값이기도 하다), 웹 리시버가 셋톱에서 도는 경우는
 * TV 브라우저이므로 `fast_ott_app` 이 맞다. 판정이 만들 수 없는 값을 만들려 들면
 * 규칙만 늘고 정확도는 오르지 않는다.
 */
export function detectClientType(signals: ClientTypeSignals): ClientType {
  const ua = signals.userAgent.toLowerCase();

  if (TV_MARKERS.some((marker) => ua.includes(marker))) return 'fast_ott_app';

  // 안드로이드 태블릿의 UA 에는 `Mobile` 이 없지만 `Android` 는 있다. TV 를 앞에서
  // 걸렀으므로 여기 남은 `Android` 는 폰이거나 태블릿이고, **둘 다 구매 기기다** —
  // Tier 0 이 재려는 것은 화면 크기가 아니라 "지금 보고 있는 이 기기에서 살 수 있는가" 다.
  if (MOBILE_MARKERS.some((marker) => ua.includes(marker))) return 'mobile_web';

  // **iPadOS 는 자기를 Macintosh 라고 말한다** (iPadOS 13+ 의 데스크톱 모드가 기본이다).
  // UA 만 보면 데스크톱과 구분되지 않으므로 이 두 신호가 필요하고, 그것이 이 함수가
  // 문자열 하나가 아니라 신호 셋을 받는 이유 전부다.
  if (signals.pointerCoarse && signals.viewportWidth <= TOUCH_MAX_WIDTH) return 'mobile_web';

  return 'pc_web';
}

/**
 * 브라우저에서 신호를 읽어 위 함수에 넘긴다. **이 파일에서 DOM 을 만지는 곳은 여기뿐이다.**
 *
 * `matchMedia` 가 없는 환경을 대비한다 — 없으면 `pointerCoarse` 는 거짓이고, 그 경우
 * 판정은 UA 에만 기댄다(위 iPadOS 분기만 잃는다).
 */
export function detectClientTypeFromWindow(): ClientType {
  return detectClientType({
    userAgent: navigator.userAgent,
    pointerCoarse:
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches,
    viewportWidth: window.innerWidth,
  });
}

/**
 * 구매 버튼 문구 (B9).
 *
 * **`Record` 라 `ClientType` 에 값이 늘면 컴파일이 깨진다.** 빠뜨린 유형이 기본 문구로
 * 조용히 나가는 것보다 빌드가 멈추는 편이 낫다 (`TriggerBadge` 의 `SOURCE_LABELS` 와
 * 같은 방침).
 *
 * 폰으로 보고 있으면 넘길 곳이 없다 — 손에 든 기기가 곧 그 기기이므로 「모바일에서
 * 구매」는 거짓말이고, 눌렀을 때 실제로 열리는 것도 같은 화면 위의 상품 시트다.
 */
/** 구매 버튼이 쓸 수 있는 키. 사전에 그 둘이 있어야 컴파일된다. */
type PurchaseLabelKey = Extract<MessageKey, 'watch.buyHere' | 'watch.buyOnMobile'>;

const PURCHASE_LABEL_KEYS: Record<ClientType, PurchaseLabelKey> = {
  mobile_web: 'watch.buyHere',
  pc_web: 'watch.buyOnMobile',
  fast_ott_app: 'watch.buyOnMobile',
  paytv_stb: 'watch.buyOnMobile',
};

/**
 * **문구가 아니라 사전 키를 돌려준다** (v9, 절대 규칙 24). 이 파일은 순수 함수 모듈이라
 * 훅을 부를 수 없고, 무엇보다 판정과 번역은 다른 일이다 — 화면이 `t()` 로 옮긴다.
 *
 * **응답을 기다리지 않고 미리 정한다** (`docs/14` B9). 누른 뒤에 글자가 변하면 시청자에게는
 * 오류로 보인다 — 그래서 서버가 무엇을 고르든 문구는 이 값 하나로 정해져 있다.
 *
 * 판정이 갈릴 일도 없다. 서버의 Tier 0 조건이 같은 값 하나(`clientType === 'mobile_web'`)
 * 이기 때문이다 (`tier0-same-device.ts`).
 */
export function purchaseLabelKey(clientType: ClientType): PurchaseLabelKey {
  return PURCHASE_LABEL_KEYS[clientType];
}

/**
 * 이 유형이 **리모컨으로 조작되는가** (v10).
 *
 * TV 계열일 때만 화살표·Enter·뒤로 키가 화면을 움직인다 (`docs/25` §6).
 * **PC·모바일 경로는 바이트 단위로 그대로다** — `detectClientType` 이 `paytv_stb` 를 절대
 * 만들지 않고 노트북에 TV 표식도 없으므로, 실질적으로 **명시적 opt-in** 이다. 「PC 는
 * 그대로다」를 코드가 아니라 판정이 보장한다.
 *
 * **새 쿼리 파라미터를 만들지 않았다.** `?client=fast_ott_app` 은 리모컨 조작을 켜는
 * 동시에 서버가 Push/QR 로 답하게 만드는데, 그게 정확히 TV 시나리오 한 벌이다. 둘을 갈라
 * 놓으면 「리모컨은 되는데 구매를 누르니 같은 기기 시트가 뜬다」 같은 반쪽 화면이 생긴다.
 *
 * `Record` 라 `ClientType` 에 값이 늘면 컴파일이 깨진다 — 빠뜨린 유형이 조용히 꺼진 채
 * 나가는 것보다 빌드가 멈추는 편이 낫다 (`PURCHASE_LABEL_KEYS` 와 같은 방침).
 */
const REMOTE_CONTROLLED: Record<ClientType, boolean> = {
  paytv_stb: true,
  fast_ott_app: true,
  pc_web: false,
  mobile_web: false,
};

export function isRemoteControlled(clientType: ClientType): boolean {
  return REMOTE_CONTROLLED[clientType];
}

/**
 * 화면이 **설치된 앱 창인가**. 키 조작을 켤지 정하는 재료다 (v19).
 *
 * **`clientType` 과 섞지 않는다.** 저쪽은 「어떤 기기인가」이고 이것은 「어떻게 실행 중인가」
 * 다 — 같은 노트북이 탭으로도 앱으로도 열린다.
 */
export interface AppDisplay {
  /**
   * **확실히 설치된 앱 창인가.**
   *
   * ★★ **`fullscreen` 을 여기 넣지 말 것** (v26 U11, 2026-09-03 실측으로 한 번 넣었다가
   * 되돌렸다). 전체화면 창은 **「설치되었는가」를 말해 주지 않는다** — 브라우저 창을
   * ⌃⌘F 로 전체화면에 두면 설치된 앱 창과 **값이 완전히 같다.**
   *
   * | | `browser` | `standalone` | `fullscreen` |
   * |---|---|---|---|
   * | 브라우저 탭 | **참** | 거짓 | 거짓 |
   * | **브라우저 탭 + ⌃⌘F** | 거짓 | 거짓 | **참** |
   * | 앱 창 | 거짓 | **참** | 거짓 |
   * | **앱 창 + ⌃⌘F** | 거짓 | 거짓 | **참** |
   *
   * **가운데 둘이 구분 불가능하다.** 넣었더니 **브라우저를 전체화면으로 둔 채 새로고침하면
   * 진입 게이트가 사라졌다** — 절대 규칙 72 가 지키려는 그 구별이 통째로 깨진 것이고,
   * 사람이 그것을 무대에서 발견했다.
   *
   * **그래서 모르는 쪽은 게이트다**(규칙 72 의 그 문장). 대가를 알고 받는다 — **설치된
   * 앱을 전체화면으로 둔 채 문서가 다시 서면**(`⌘R` · 알림 진입) 게이트가 한 번 뜬다.
   * 클릭 한 번이고, **반대 방향의 실패는 계약을 깨뜨린다.**
   *
   * ~~키 조작이 꺼진다.~~ **그 대가는 받지 않는다** — 아래 `fullscreen` (2026-09-16).
   */
  installed: boolean;
  /**
   * **창 전체화면인가** — `(display-mode: fullscreen)`. `document.fullscreenElement`(전체화면
   * API)가 아니라 **⌃⌘F 로 창을 띄운 상태**다.
   *
   * ★ **이것은 「설치되었는가」가 아니다.** 위 표의 가운데 둘이 구분 불가능하다는 사실은
   * 그대로이고, 그래서 이 값은 **`installed` 옆에 따로 선다** — 게이트(`autoStartsWithoutGesture`)
   * 는 이 값을 읽지 않는다(절대 규칙 72 · `auto-start.ts` 는 `installed` 만 destructure 한다).
   * 읽는 곳은 **키 조작 하나**(`usesKeyboardNav`)이고, 그 이유는 두 물음의 **대가가 반대
   * 방향이기 때문**이다 — 아래 `usesKeyboardNav` 의 ★★.
   */
  fullscreen: boolean;
  /** 손으로 쓰는 기기인가 (`pointer: coarse`) */
  handheld: boolean;
}

/**
 * **화살표·Enter·뒤로 키가 화면을 움직이는가** (v19).
 *
 * ── 왜 `isRemoteControlled` 하나로는 안 되는가 ────────────────
 *
 * v10 은 통로를 `?client=fast_ott_app` 하나로 뒀다 — 리모컨 조작과 TV 상거래 표면이
 * **한 시나리오 한 벌**이기 때문이고, 그 판단은 지금도 옳다. 그런데 **노트북에 설치한
 * 앱에는 주소창이 없어 그 통로 자체가 없다**(`start_url` 은 `/` 이고, 거기에 쿼리를
 * 넣으면 폰 설치까지 TV 모드가 된다).
 *
 * 그래서 **키 조작만** 따로 켠다. `clientType` 은 `pc_web` 그대로다 — 바꾸면 구매 문구가
 * 「모바일에서 구매」로 가고(`PURCHASE_LABEL_KEYS`) **이력 승격까지 꺼진다**
 * (`promotesProgressOnLogin`). 그것이 v10 주석이 경고한 **「리모컨은 되는데 구매를 누르니
 * 반쪽인 화면」**이고, 여기서 갈라야 할 것은 정확히 그 반대쪽이다.
 *
 * **마우스를 뺏지 않는다.** 켜지는 것은 키 입력 처리뿐이고 링은 `:focus-visible` 로 남는다
 * (`styles.css` 의 그 대비 — TV 는 마우스가 없어 `:focus`, 이쪽은 있으므로 휴리스틱).
 *
 * **탭은 그대로다** — 노트북 브라우저 탭에는 `?client=fast_ott_app` 통로가 이미 있고,
 * 넓히면 `remote.offWithoutTvClient`(PC 회귀 방벽)가 재려던 것이 사라진다.
 *
 * ── ★★ 전체화면인 창도 켠다 (2026-09-16 실측) ───────────────
 *
 * `installed` 하나로 물으면 **설치된 앱을 ⌃⌘F 로 띄운 채 문서가 다시 서는 순간 키가
 * 통째로 죽는다**(`⌘R` · 새 번들 적용 · 알림 진입). 그리고 **노트북 전체화면은
 * `docs/53` §2.9.6 이 데모에 지시하는 바로 그 조작**이고, 그 문서의 표가 이미 이것을
 * 적어 두었다 — 「**둘이 함께 조용히 꺼진다** — 자동 시작과 **키 조작**」. 그 둘을
 * 여기서 가른다. 실측(2026-09-16, 배포판):
 *
 * | 창 | `standalone` | `fullscreen` | `data-tv-focus` |
 * |---|---|---|---|
 * | 앱 창 | 참 | 거짓 | `hybrid` |
 * | 앱 창 → ⌃⌘F (그대로) | 거짓 | 참 | `hybrid` — 마운트 때 잡은 값이라 **살아 있다** |
 * | **앱 창 → ⌃⌘F → 재적재** | 거짓 | 참 | **없음 — 화살표가 아무 데도 가지 않는다** |
 *
 * **가운데 줄이 이 결함을 늦게 만든다** — 전체화면으로 바꾼 직후에는 멀쩡하고, 문서가
 * 다시 설 때 죽는다. 그래서 「아까는 됐는데」가 되고 원인이 전체화면으로 보이지 않는다.
 *
 * **게이트(규칙 72)와 반대로 판단하는 것이 맞다.** 같은 모호함(브라우저 탭 + ⌃⌘F 를
 * 구분할 수 없다)을 놓고 대가가 반대 방향이기 때문이다 —
 *   · 게이트에서 틀리면 **묻지 않아야 할 때 묻지 않는다**: 소리가 막히고 화면이 검다.
 *     그래서 **모르면 묻는다.**
 *   · 키 조작에서 틀리면 **전체화면 브라우저 탭에서도 화살표가 포커스를 옮긴다**:
 *     마우스는 그대로고, 링은 `:focus-visible` 이라 키를 쓸 때만 뜨며, 재생은 멈추지
 *     않는다(절대 규칙 31 — `decide` 가 옮겼을 때만 기본 동작을 막는다).
 *     **아무도 잃지 않는다.** 그래서 **모르면 켠다.**
 *
 * PC 회귀 방벽은 그대로다 — 전체화면이 아닌 브라우저 탭은 여전히 꺼져 있고
 * (`remote.offWithoutTvClient`), 새로 켜지는 창은 **주소창이 보이지 않는 창**뿐이다.
 */
export function usesKeyboardNav(clientType: ClientType, display: AppDisplay): boolean {
  if (isRemoteControlled(clientType)) return true;
  return (display.installed || display.fullscreen) && !display.handheld;
}

/**
 * v38 — **설치된 앱의 창인가** — 개인화 채널 엔진의 기본을 정한다(`receiverEngineFor` · 절대 규칙 93).
 *
 * 판단은 `usesKeyboardNav` 와 같다(`installed || fullscreen`) — 노트북 설치 앱은 첫 조작에서 스스로 전체화면이 되고
 * (`use-entry-fullscreen.ts`) 그때 `display-mode` 가 `standalone` 에서 `fullscreen` 으로 갈아 끼워지므로, `installed` 하나로
 * 물으면 **전체화면인 채 문서가 다시 서는 순간**(⌘R · 새 번들) BLOOM 으로 돌아간다. 대가는 키 조작과 같은 쪽이다 —
 * 전체화면인 브라우저 탭을 새로 고치면 Flower 가 돈다(Flower 는 실패하면 이유를 남기고 BLOOM 으로 폴백한다 · 규칙 93).
 * **`handheld` 는 빼지 않는다** — 안드로이드 홈 화면 앱도 Flower 의 대상이다(아이폰은 네이티브 HLS 라 어차피 BLOOM).
 * 게이트(규칙 72)는 이것을 읽지 않는다.
 */
export function isAppWindow(display: AppDisplay): boolean {
  return display.installed || display.fullscreen;
}

/**
 * 실행 형태를 읽는다. **DOM 을 읽는 자리는 여기 하나다** (`usesKeyboardNav` 가 규칙을 갖는다).
 */
export function readAppDisplay(): AppDisplay {
  if (typeof window === 'undefined')
    return { installed: false, fullscreen: false, handheld: false };
  const media = (query: string): boolean => {
    try {
      return window.matchMedia(query).matches;
    } catch {
      return false;
    }
  };
  return {
    // ★ **`standalone` 하나다.** `fullscreen` 을 여기 더하면 **전체화면인 브라우저 창까지
    //   설치된 앱이 된다** — 위 `installed` 의 표. 그것은 아래 칸에 따로 싣고, 게이트는
    //   이 칸만 읽는다(절대 규칙 72). `minimal-ui` 는 어느 칸에도 넣지 않는다: 우리
    //   매니페스트가 `standalone` 이라 그 모드로 뜨는 경우가 없고, **쓰이지 않는 갈래는
    //   틀려도 아무도 모른다.**
    installed: media('(display-mode: standalone)'),
    fullscreen: media('(display-mode: fullscreen)'),
    handheld: media('(pointer: coarse)'),
  };
}

/**
 * 로그인할 때 **익명 시청 이력을 계정으로 승격하는가** (v17 R13 · `docs/38` D6 개정).
 *
 * ── ★ v18 이 승격 자체를 없앴다 — 이 함수는 지금 무해하다 ────
 *
 * **비로그인에는 이력이 없다**(절대 규칙 46 · `progress-scope.ts`). 그래서 승격할 것이
 * 없고, 서버는 로그인 요청의 `deviceId` 를 **받아서 버린다**
 * (`BloomAuthService.loginWithGoogle` 의 `_deviceId`).
 *
 * **그래도 배선을 지운다** — 되살릴 자리이고, 지우면 v17 R13 이 값을 치른 판단
 * (「공유 기기에서는 승격하지 않는다」)이 함께 사라진다. 지금 이 함수가 하는 일은
 * **TV 에서 `deviceId` 를 싣지 않는 것** 하나이고, 서버가 어느 쪽이든 무시하므로
 * 관측 가능한 차이가 없다.
 *
 * **아래 서술은 v16~v17 시점의 것이다.** 승격을 되살리는 날 그대로 유효하다.
 *
 * ── 왜 기기마다 다른가 ──────────────────────────────────────
 *
 * 승격이 도는 순간은 **한 기기에서 익명으로 본 뒤 로그인할 때** 하나다. 그때 「익명으로 본
 * 사람」이 누구인지가 기기마다 다르다.
 *
 * - **폰·PC** — 거의 확실히 **로그인하는 그 사람**이다. 승격하지 않으면 「보다가
 *   로그인했더니 처음으로 돌아갔다」가 되고, 그것이 `docs/38` D6 이 막으려던 것이다
 * - **TV** — **가족 중 누구든**이다. 아이들이 본 진행이 아빠 계정으로 들어간다.
 *   서버가 `Math.max` 로 합치므로 **되감기지 않고 앞으로만 뛴다** — 증상은 「내가 안 본
 *   데까지 봤다고 표시된다」와 **스포일러**이고, 되돌릴 방법이 없다
 *
 * ~~**TV 에서도 익명 이어보기는 그대로 돈다** — 기기 축이 살아 있다.~~
 * **v18 이 기기 축을 없앴다** — 비로그인은 어느 기기에서도 이어보기가 없고 편성표대로
 * 재생한다. 로그인하면 **그 계정이 보던 것**이 나온다는 것만 그대로다.
 *
 * ── `isRemoteControlled` 와 묶지 않는다 ──────────────────────
 *
 * **지금은 값이 같지만 뜻이 다르다** — 저쪽은 「리모컨으로 조작하는가」이고 이쪽은
 * 「여럿이 함께 쓰는가」다. 한 이름으로 묶으면 그 구분이 지워지고, 리모컨을 쓰는 개인
 * 기기(또는 그 반대)가 생기는 날 **한쪽만 고쳐야 하는데 두 곳이 함께 움직인다.**
 * **나중에 두 표의 값이 갈리면 그것이 정상이다.**
 */
const PROMOTES_PROGRESS_ON_LOGIN: Record<ClientType, boolean> = {
  // 공유 기기 — 익명 시청이 로그인하는 사람의 것이라는 보장이 없다
  paytv_stb: false,
  fast_ott_app: false,
  // 개인 기기 — 익명 시청이 곧 그 사람이다
  pc_web: true,
  mobile_web: true,
};

export function promotesProgressOnLogin(clientType: ClientType): boolean {
  return PROMOTES_PROGRESS_ON_LOGIN[clientType];
}

/** `?client=fast_ott_app` — 판정을 덮는 쿼리 파라미터 이름. */
export const CLIENT_TYPE_PARAM = 'client';

/**
 * 쿼리로 지목한 유형. 계약에 없는 값은 **무시한다**(`null`).
 *
 * 모르는 값을 그대로 실어 보내면 서버가 `400 VALIDATION_ERROR` 로 되돌려 주고, 화면에는
 * 「요청이 처리되지 않았습니다」만 남아 **오타 하나가 서버 장애처럼 보인다.** 덮지 못했으면
 * 덮지 않은 것이 맞다 — 그러면 판정이 그대로 돌고 화면은 정상 동작한다.
 *
 * **순수 함수다.** 이 파일에서 DOM 을 만지는 곳은 아래 둘뿐이고, 여기서 갈리는 것이
 * 「CTV 를 PC 로 모사한다」의 전부다 (CLAUDE.md 머리 — "CTV 는 PC 로 모사한다").
 */
export function overriddenClientType(raw: string | null | undefined): ClientType | null {
  return isClientType(raw) ? raw : null;
}

/**
 * 이번 진입의 클라이언트 유형. **쿼리가 판정을 이긴다.**
 *
 * ── 왜 덮을 수 있어야 하는가 ─────────────────────────────────
 *
 * 이 MVP 는 **CTV 를 PC 로 모사한다**(CLAUDE.md 머리). 그런데 PC 브라우저의 판정 결과는
 * `pc_web` 이라, 셋톱·CTV 로 들어오는 경로(`paytv_stb`·`fast_ott_app`)는 **실기에서 한
 * 번도 밟히지 않는다.** 서버 쪽 tier 판정은 이 값 하나만 보므로, 그것을 바꿀 수 있으면
 * TV 시나리오 전체가 같은 노트북에서 재현된다.
 *
 * `?debug=1` 과 달리 **런타임 설정(`runtimeOverrides`)에 두지 않았다.** 저쪽은 서버가
 * 내려주는 값을 덮는 것이고 이것은 브라우저가 스스로 아는 값을 덮는 것이라 출처가 다르다.
 *
 * 덮어도 위험하지 않다. 이 값으로 열리는 문은 없다 — 로그인 게이트도 파트너 키도 이것을
 * 보지 않고, 바뀌는 것은 **서버가 어느 tier 로 답하는가** 뿐이다.
 */
export function resolveClientType(search: string = window.location.search): ClientType {
  return (
    overriddenClientType(new URLSearchParams(search).get(CLIENT_TYPE_PARAM)) ??
    detectClientTypeFromWindow()
  );
}
